import { useEffect, useMemo, useRef, useState } from "react";
import {
  createCampaign,
  estimateCohort,
  getCampaign,
  getCampaigns,
  getPrograms,
  getRuns,
  getTemplates,
  transitionCampaign,
  updateCampaign,
} from "../api/client.js";
import { Shell } from "../components/Shell.jsx";
import {
  GateBanner,
  GovernanceChecklist,
  Loading,
  ErrorCard,
  MetricValue,
  Panel,
  StatusPill,
  WizardSteps,
} from "../components/ui.jsx";
import { Link, useNavigate } from "react-router-dom";
import { DOMAIN_LABELS, withDiabetesCareGapFallback } from "../domainModel.js";
import {
  WORKDAY_APP_CHANNEL,
  WORKDAY_APP_CHANNEL_KEY,
  WORKDAY_APP_CHANNEL_LABEL,
  normalizeWorkdayAppPlan,
  templateSupportsWorkdayApp,
} from "../channelModel.js";
import { ACTIVATION_PATHS, campaignRoute, opportunitiesRoute } from "../navConfig.js";
import { browserTimeZone, sendTimingLabel } from "../sendSchedule.js";

const STEPS = [
  "Program and evidence period",
  "Aggregate cohort",
  "Workday app messages",
  "Review and governance",
];
const DEFAULT_PLAN = [
  { channel: WORKDAY_APP_CHANNEL_KEY, templateId: "", sequenceDay: 0 },
  { channel: WORKDAY_APP_CHANNEL_KEY, templateId: "", sequenceDay: 4 },
  { channel: WORKDAY_APP_CHANNEL_KEY, templateId: "", sequenceDay: 12 },
];
const emptyFilters = () => ({
  stageIds: [],
  bands: [],
  subdomains: [],
  confidenceLevels: [],
  recommendedActions: [],
});

// Channel glyph so each touch card reads as its own mini dashboard.
const CHANNEL_META = {
  [WORKDAY_APP_CHANNEL_KEY]: { glyph: "🏢" },
};

function templateSupportsChannel(template, channel) {
  return channel === WORKDAY_APP_CHANNEL_KEY && templateSupportsWorkdayApp(template);
}

const GOAL_LABELS = {
  goal1_enrollment: "Goal 1: enrollment campaign",
  goal2_usage: "Goal 2: active usage campaign",
};

function campaignGoalOptions(enums, domain, selectedAction = "") {
  if (!domain) return [];
  const byAction = new Map();
  Object.entries(enums.recommendedActions?.[domain] || {}).forEach(([stageId, action]) => {
    const numericStageId = Number(stageId);
    if (numericStageId <= 0) return;
    if (!byAction.has(action)) byAction.set(action, []);
    byAction.get(action).push(numericStageId);
  });
  withDiabetesCareGapFallback(byAction, domain, selectedAction);
  return [...byAction.entries()].map(([label, stageIds]) => ({
    label,
    stageIds: [...stageIds].sort((a, b) => a - b),
  }));
}

function templateMatchesContext(template, activationGoal, recommendedAction) {
  if (activationGoal && template.activationGoal && template.activationGoal !== activationGoal) return false;
  if (recommendedAction) return template.recommendedAction === recommendedAction;
  return true;
}

function bestTemplateForTouch(templates, touch, activationGoal, recommendedAction, sourceSequence) {
  let candidates = (templates || []).filter(
    (template) =>
      templateSupportsChannel(template, touch.channel) &&
      templateMatchesContext(template, activationGoal, recommendedAction)
  );
  if (sourceSequence) {
    const sequenceMatches = candidates.filter(
      (template) => Number(template.sourceSequence) === Number(sourceSequence)
    );
    if (sequenceMatches.length) candidates = sequenceMatches;
  }
  if (!candidates.length) return "";
  return [...candidates].sort((a, b) => {
    const statusRank = Number(b.status === "active") - Number(a.status === "active");
    if (statusRank) return statusRank;
    const goalRank =
      Number(b.activationGoal === activationGoal) - Number(a.activationGoal === activationGoal);
    if (goalRank) return goalRank;
    const actionRank =
      Number(b.recommendedAction === recommendedAction) - Number(a.recommendedAction === recommendedAction);
    if (actionRank) return actionRank;
    return (
      Math.abs((a.sequenceDay || 0) - touch.sequenceDay) - Math.abs((b.sequenceDay || 0) - touch.sequenceDay)
    );
  })[0].id;
}

function applyTemplateContent(touch, template) {
  return {
    ...touch,
    templateId: template?.id || "",
    subject: template?.subject || "",
    body: template?.body || "",
    pushMessage: template?.pushMessage || template?.body || "",
    safetyDisclaimer: template?.safetyDisclaimer || "",
  };
}

function resolveTouchTemplate(templates, touch, activationGoal, recommendedAction, sourceSequence) {
  const requestedTemplateId = bestTemplateForTouch(
    templates,
    touch,
    activationGoal,
    recommendedAction,
    sourceSequence
  );
  if (requestedTemplateId) {
    return applyTemplateContent(
      touch,
      templates.find((template) => template.id === requestedTemplateId)
    );
  }

  return applyTemplateContent(touch, null);
}

export function CampaignBuilder({ tenant, enums, params, auth }) {
  const navigate = useNavigate();
  const editCampaignId = params.get("campaignId") || "";
  const requestedStep = params.get("step") || "program";
  const requestedStepIndex = Math.max(0, ["program", "cohort", "channels", "review"].indexOf(requestedStep));
  const initialDomain = params.get("programDomain");
  const initialRunMonth = params.get("runMonth") || "";
  const initialRecommendedAction = params.get("recommendedAction") || "";
  const initialActivationGoal = params.get("activationGoal") || "";
  const startedFromRecommendation =
    params.get("source") === "recommendation" ||
    Boolean(initialDomain && (initialActivationGoal || initialRecommendedAction));
  const [step, setStep] = useState(editCampaignId ? requestedStepIndex : 0);
  const [programs, setPrograms] = useState(null);
  const [existingCampaigns, setExistingCampaigns] = useState([]);
  const [programId, setProgramId] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [priority, setPriority] = useState(50);
  const [activationGoal, setActivationGoal] = useState(initialActivationGoal);
  const [runs, setRuns] = useState(null);
  const [selectedRunMonth, setSelectedRunMonth] = useState(initialRunMonth);
  const [filters, setFilters] = useState(emptyFilters);
  const [plan, setPlan] = useState(() => normalizeWorkdayAppPlan(DEFAULT_PLAN));
  const [campaignMode, setCampaignMode] = useState("predefined");
  const [templates, setTemplates] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [draft, setDraft] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const preserveLoadedDraftRef = useRef(null);

  useEffect(() => {
    Promise.all([
      getPrograms(),
      editCampaignId ? getCampaign(editCampaignId) : Promise.resolve(null),
      getCampaigns(),
    ])
      .then(([data, savedCampaign, campaignData]) => {
        setPrograms(data.programs);
        setExistingCampaigns(campaignData.campaigns || []);
        if (savedCampaign) {
          preserveLoadedDraftRef.current = savedCampaign.programDomain;
          setProgramId(savedCampaign.programId);
          setName(savedCampaign.name || "");
          setPriority(savedCampaign.priority ?? 50);
          setActivationGoal(savedCampaign.activationGoal || "");
          setSelectedRunMonth(savedCampaign.runMonth || "");
          setFilters({ ...emptyFilters(), ...savedCampaign.cohortFilters });
          setPlan(normalizeWorkdayAppPlan(savedCampaign.channelPlan));
          setCampaignMode("user-defined");
          setDraft(savedCampaign);
          setGovernance(savedCampaign.governance);
          setStep(requestedStepIndex);
        } else if (initialDomain) {
          const match = data.programs.find((p) => p.programDomain === initialDomain);
          if (match) setProgramId(match.id);
        }
      })
      .catch(setError);
  }, [editCampaignId, initialDomain, requestedStepIndex]);

  const program = programs?.find((p) => p.id === programId);
  const domain = program?.programDomain;
  const programName = domain ? DOMAIN_LABELS[domain] || program?.name : program?.name;
  const normalizedCampaignName = name.trim().toLocaleLowerCase();
  const duplicateCampaignName = existingCampaigns.some(
    (campaign) =>
      campaign.id !== editCampaignId && campaign.name?.trim().toLocaleLowerCase() === normalizedCampaignName
  );
  const campaignNameError = !normalizedCampaignName
    ? "Enter a descriptive campaign name."
    : duplicateCampaignName
      ? "That campaign name is already in use. Choose a unique name."
      : "";
  const stages = domain ? enums.stages[domain].filter((s) => s.id > 0) : [];
  const subdomains = domain ? enums.subdomains[domain] : [];
  const currentRecommendedAction = filters.recommendedActions[0] || "";
  const goalOptions = campaignGoalOptions(
    enums,
    domain,
    currentRecommendedAction || initialRecommendedAction
  );

  useEffect(() => {
    if (!domain) return;
    const preserveLoadedDraft = preserveLoadedDraftRef.current === domain;
    if (preserveLoadedDraft) preserveLoadedDraftRef.current = null;
    setRuns(null);
    setTemplates(null);
    getRuns(domain)
      .then((data) => {
        setRuns(data.runs);
        setSelectedRunMonth((current) =>
          data.runs.some((run) => run.runMonth === current) ? current : data.runs[0]?.runMonth || ""
        );
      })
      .catch(setError);
    getTemplates(domain)
      .then((data) => setTemplates(data.templates))
      .catch(setError);
    const initialGoal = campaignGoalOptions(enums, domain, initialRecommendedAction).find(
      (option) => option.label === initialRecommendedAction
    );
    if (!preserveLoadedDraft) {
      setFilters(
        initialGoal
          ? {
              ...emptyFilters(),
              stageIds: initialGoal.stageIds,
              recommendedActions: [initialGoal.label],
            }
          : emptyFilters()
      );
    }
    setEstimate(null);
    if (!preserveLoadedDraft) {
      setDraft(null);
      setGovernance(null);
      setActivationGoal(initialActivationGoal);
    }
  }, [domain, enums, initialActivationGoal, initialRecommendedAction]);

  // Auto-fill (and re-validate) a template for every touch — on the cold path
  // as well as deep links. Runs when the domain/templates load or the goal /
  // recommended-action context changes; it does NOT depend on `plan`, so a
  // template the user manually clears or overrides is left untouched.
  useEffect(() => {
    if (!domain || !templates) return;
    setPlan((prev) => {
      const usedTemplateIds = new Set();
      return prev.map((touch, index) => {
        const selected = templates.find((template) => template.id === touch.templateId);
        if (
          selected &&
          !usedTemplateIds.has(selected.id) &&
          templateSupportsChannel(selected, touch.channel) &&
          templateMatchesContext(selected, activationGoal, currentRecommendedAction)
        ) {
          usedTemplateIds.add(selected.id);
          return touch;
        }
        const resolved = resolveTouchTemplate(
          templates.filter((template) => !usedTemplateIds.has(template.id)),
          touch,
          activationGoal,
          currentRecommendedAction,
          index + 1
        );
        if (resolved.templateId) usedTemplateIds.add(resolved.templateId);
        return resolved;
      });
    });
  }, [domain, templates, activationGoal, currentRecommendedAction]);

  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    setEstimate(null);
    estimateCohort({
      programDomain: domain,
      filters,
      runMonth: selectedRunMonth || undefined,
      activationGoal: activationGoal || undefined,
    })
      .then((data) => {
        if (!cancelled) setEstimate(data);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError);
      });
    return () => {
      cancelled = true;
    };
  }, [domain, filters, activationGoal, selectedRunMonth]);

  const selectedRun = runs?.find((run) => run.runMonth === selectedRunMonth) || runs?.[0];
  const gateFailed = selectedRun?.qualityGate?.status === "fail";

  // Header context — which opportunity (program domain) and which action this
  // campaign targets, so it reads the same whether entered cold from the
  // Activation sidebar or pre-filled from an Opportunities deep link.
  const domainLabel = domain ? DOMAIN_LABELS[domain] : null;
  const headerContextParts = [domainLabel, GOAL_LABELS[activationGoal], currentRecommendedAction].filter(
    Boolean
  );
  const sourceLabel = editCampaignId
    ? "Continuing saved campaign setup"
    : startedFromRecommendation
      ? "Started from recommendation"
      : "Started from Campaigns";
  const campaignGoalLabel =
    activationGoal === "goal1_enrollment"
      ? "Enroll eligible people who are not enrolled"
      : currentRecommendedAction || "Not selected";
  const cancelRoute = editCampaignId
    ? `${ACTIVATION_PATHS.campaigns}/${encodeURIComponent(editCampaignId)}`
    : startedFromRecommendation
      ? opportunitiesRoute({
          programDomain: domain || initialDomain,
          runMonth: selectedRun?.runMonth || initialRunMonth,
        })
      : ACTIVATION_PATHS.campaigns;

  const sensitivityHigh = useMemo(() => {
    if (!domain) return false;
    if (domain === "diabetes") return true;
    return domain === "telehealth" && filters.subdomains.includes("Behavioral health");
  }, [domain, filters.subdomains]);

  const toggle = (key, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((x) => x !== value) : [...prev[key], value],
    }));

  const selectCampaignGoal = (goal) => {
    setActivationGoal("goal2_usage");
    setFilters((prev) => ({
      ...prev,
      stageIds: goal.stageIds,
      recommendedActions: [goal.label],
    }));
  };

  const selectEnrollmentGoal = () => {
    setActivationGoal("goal1_enrollment");
    setFilters(emptyFilters());
  };

  const setTouch = (index, patch) =>
    setPlan((prev) =>
      prev.map((touch, i) => {
        if (i !== index) return touch;
        const updated = { ...touch, ...patch };
        if (patch.channel === undefined) return updated;
        const templateId = bestTemplateForTouch(
          templates,
          updated,
          activationGoal,
          currentRecommendedAction,
          index + 1
        );
        return applyTemplateContent(
          updated,
          (templates || []).find((template) => template.id === templateId)
        );
      })
    );

  const createPredefinedCampaign = (count) => {
    setCampaignMode("predefined");
    const usedTemplateIds = new Set();
    const predefined = Array.from({ length: count }, (_, index) => {
      const touch = {
        ...DEFAULT_PLAN[index],
        channel: WORKDAY_APP_CHANNEL_KEY,
      };
      const resolved = resolveTouchTemplate(
        (templates || []).filter((template) => !usedTemplateIds.has(template.id)),
        touch,
        activationGoal,
        currentRecommendedAction,
        index + 1
      );
      if (resolved.templateId) usedTemplateIds.add(resolved.templateId);
      return resolved;
    });
    setPlan(predefined);
  };

  const startUserDefinedCampaign = () => {
    setCampaignMode("user-defined");
    setPlan((prev) => {
      if (prev.length) return prev;
      const touch = {
        ...DEFAULT_PLAN[0],
        channel: WORKDAY_APP_CHANNEL_KEY,
      };
      return [resolveTouchTemplate(templates, touch, activationGoal, currentRecommendedAction, 1)];
    });
  };

  const addUserDefinedTouch = () => {
    setCampaignMode("user-defined");
    setPlan((prev) => {
      if (prev.length >= DEFAULT_PLAN.length) return prev;
      const index = prev.length;
      const touch = {
        ...DEFAULT_PLAN[index],
        channel: WORKDAY_APP_CHANNEL_KEY,
      };
      return [
        ...prev,
        resolveTouchTemplate(templates, touch, activationGoal, currentRecommendedAction, index + 1),
      ];
    });
  };

  const removeUserDefinedTouch = (removeIndex) => {
    setCampaignMode("user-defined");
    setPlan((prev) => prev.filter((_, index) => index !== removeIndex));
  };

  const planReady = plan.length > 0 && plan.every((touch) => touch.templateId);

  useEffect(() => {
    setGovernance(null);
  }, [name, priority, activationGoal, filters, plan, selectedRunMonth, programId]);

  const buildDraftAndCheck = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        activationGoal,
        cohortFilters: filters,
        channelPlan: plan,
        runId: selectedRun?.runId,
        runMonth: selectedRun?.runMonth,
        // Day 0 is the publication time; the API sets the start when the campaign is published.
        startsAt: null,
        timeZone: browserTimeZone(),
        priority: Number(priority),
      };
      // Reuse the existing draft across re-runs instead of creating a new
      // campaign each time — governance is recomputed and returned inline.
      const saved = draft
        ? await updateCampaign(draft.id, payload)
        : await createCampaign({ ...payload, programId });
      setDraft(saved);
      setGovernance(saved.governance);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitForReview = async () => {
    setBusy(true);
    try {
      await transitionCampaign(draft.id, "submit");
      navigate(campaignRoute(draft.id));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const stepBody = () => {
    if (!programs) return <Loading />;

    if (step === 0)
      return (
        <Panel
          headingLevel={3}
          title="Program and evidence period"
          desc={
            editCampaignId
              ? "The program is fixed for this saved draft. Its evidence period and campaign name can still be updated."
              : startedFromRecommendation
                ? "The recommendation supplies these fields. Return to Opportunity Explorer to change them."
                : "Choose the program and evidence period for this campaign."
          }
        >
          <div className="program-choice-grid">
            {programs.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`bi-panel program-choice-card${p.id === programId ? " selected" : ""}`}
                onClick={() => setProgramId(p.id)}
                disabled={startedFromRecommendation || Boolean(editCampaignId)}
                aria-pressed={p.id === programId}
              >
                <div className="builder-field-heading">
                  <h3>{DOMAIN_LABELS[p.programDomain] || p.name}</h3>
                  {(startedFromRecommendation || editCampaignId) && p.id === programId && (
                    <span className="builder-field-status inherited">
                      {editCampaignId ? "Saved" : "Inherited"}
                    </span>
                  )}
                </div>
                <div className="desc">Aggregate program campaign</div>
                <div className="program-choice-meta">
                  <MetricValue value={p.opportunityCount} grain="employees with detected opportunities" />
                  <span>Evidence period {p.latestRun.runMonth}</span>
                  <StatusPill status={p.latestRun.qualityGate.status} />
                </div>
              </button>
            ))}
          </div>
          {domain && runs && selectedRun && (
            <div className="builder-program-fields">
              <GateBanner gate={selectedRun.qualityGate} />
              <div className="builder-field-grid">
                <label className="builder-labeled-field">
                  <span className="builder-field-heading">
                    <span>Evidence period</span>
                    <span
                      className={`builder-field-status ${startedFromRecommendation ? "inherited" : "editable"}`}
                    >
                      {startedFromRecommendation ? "Inherited" : "Editable"}
                    </span>
                  </span>
                  <select
                    className="select"
                    value={selectedRunMonth}
                    disabled={startedFromRecommendation}
                    onChange={(event) => setSelectedRunMonth(event.target.value)}
                  >
                    {runs.map((run) => (
                      <option key={run.runId} value={run.runMonth}>
                        {run.runMonth}
                        {run.qualityGate.status === "fail" ? " — blocked" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="builder-labeled-field">
                  <span className="builder-field-heading">
                    <span>Campaign name</span>
                    <span className="builder-field-status editable">Required · unique</span>
                  </span>
                  <input
                    className="input campaign-name-input"
                    value={name}
                    placeholder={`${programName} campaign`}
                    required
                    maxLength={120}
                    aria-invalid={nameTouched && Boolean(campaignNameError)}
                    aria-describedby={
                      nameTouched && campaignNameError
                        ? "campaign-name-guidance campaign-name-error"
                        : "campaign-name-guidance"
                    }
                    onBlur={() => setNameTouched(true)}
                    onChange={(event) => setName(event.target.value)}
                  />
                  <span id="campaign-name-guidance" className="builder-field-guidance">
                    Use a unique name that identifies the program and purpose.
                  </span>
                  {nameTouched && campaignNameError && (
                    <span id="campaign-name-error" className="builder-field-error" role="alert">
                      {campaignNameError}
                    </span>
                  )}
                </label>
              </div>
            </div>
          )}
        </Panel>
      );

    if (step === 1)
      return (
        <Panel
          headingLevel={3}
          title="Aggregate cohort"
          desc="Review the campaign audience and privacy controls before choosing communications."
        >
          {activationGoal && estimate ? (
            <section className={`cohort-lead${estimate.suppressed ? " suppressed" : ""}`}>
              <div>
                <span className="recommendation-label">Aggregate campaign audience</span>
                <MetricValue
                  value={estimate.memberCount}
                  grain="covered people"
                  suppressed={estimate.suppressed}
                  minimum={estimate.minCohortSize}
                />
                <p>
                  {estimate.meetsMinimum
                    ? "This audience meets the approved privacy threshold."
                    : `This audience is below the approved privacy threshold of ${estimate.minCohortSize} covered people and cannot be used for a campaign.`}
                </p>
              </div>
              <div className="cohort-guardrail-summary">
                <div>
                  <span>Excluded by guardrails</span>
                  <MetricValue value={estimate.guardrailExcludedCount} grain="covered people" />
                </div>
                <div>
                  <span>Excluded by enrollment rule</span>
                  <MetricValue value={estimate.enrollmentExcludedCount} grain="covered people" />
                </div>
              </div>
            </section>
          ) : (
            <div className="cohort-awaiting" role="status">
              Select a campaign goal to calculate the aggregate audience.
            </div>
          )}

          <section className="builder-goal-section" aria-labelledby="campaign-goal-heading">
            <div className="builder-field-heading">
              <h3 id="campaign-goal-heading">Campaign goal</h3>
              <span
                className={`builder-field-status ${startedFromRecommendation ? "inherited" : "editable"}`}
              >
                {startedFromRecommendation ? "Inherited" : "Editable"}
              </span>
            </div>
            {startedFromRecommendation ? (
              <div className="inherited-goal">
                <strong>{campaignGoalLabel}</strong>
                <span>To use a different recommendation, cancel and return to Opportunity Explorer.</span>
              </div>
            ) : (
              <div className="goal-choice-groups">
                <div>
                  <span className="field-label">Enrollment</span>
                  <button
                    type="button"
                    className={`campaign-goal-button${activationGoal === "goal1_enrollment" ? " selected" : ""}`}
                    aria-pressed={activationGoal === "goal1_enrollment"}
                    onClick={selectEnrollmentGoal}
                  >
                    Enroll eligible people who are not enrolled
                  </button>
                </div>
                <div>
                  <span className="field-label">Program use</span>
                  <div className="chip-row">
                    {goalOptions.map((goal) => (
                      <button
                        type="button"
                        key={goal.label}
                        className={"chip" + (currentRecommendedAction === goal.label ? " on" : "")}
                        aria-pressed={currentRecommendedAction === goal.label}
                        onClick={() => selectCampaignGoal(goal)}
                      >
                        {goal.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          <details className="cohort-logic">
            <summary>Show aggregate cohort logic</summary>
            <div className="cohort-logic-body">
              <dl className="cohort-definition-list">
                <div>
                  <dt>Enrollment rule</dt>
                  <dd>
                    {activationGoal === "goal2_usage"
                      ? "Currently enrolled covered people"
                      : activationGoal === "goal1_enrollment"
                        ? "Eligible covered people who are not enrolled"
                        : "Select a campaign goal"}
                  </dd>
                </div>
                <div>
                  <dt>Recommended action</dt>
                  <dd>{currentRecommendedAction || "Not applicable to enrollment campaigns"}</dd>
                </div>
                <div>
                  <dt>Included stages</dt>
                  <dd>
                    {currentRecommendedAction
                      ? stages
                          .filter((stage) => filters.stageIds.includes(stage.id))
                          .map((stage) => `${stage.id}. ${stage.name}`)
                          .join(" · ")
                      : "Determined by the enrollment rule"}
                  </dd>
                </div>
              </dl>

              {!startedFromRecommendation && (
                <div className="cohort-optional-filters">
                  <span className="field-label" id="priority-bands-label">
                    Optional priority bands
                  </span>
                  <div className="chip-row" role="group" aria-labelledby="priority-bands-label">
                    {enums.bands.map((band) => (
                      <button
                        type="button"
                        key={band}
                        className={"chip" + (filters.bands.includes(band) ? " on" : "")}
                        aria-pressed={filters.bands.includes(band)}
                        onClick={() => toggle("bands", band)}
                      >
                        {band}
                      </button>
                    ))}
                  </div>
                  <span className="field-label" id="condition-groups-label">
                    Optional condition groups
                  </span>
                  <div className="chip-row" role="group" aria-labelledby="condition-groups-label">
                    {subdomains.map((subdomain) => (
                      <button
                        type="button"
                        key={subdomain}
                        className={"chip" + (filters.subdomains.includes(subdomain) ? " on" : "")}
                        aria-pressed={filters.subdomains.includes(subdomain)}
                        onClick={() => toggle("subdomains", subdomain)}
                      >
                        {subdomain}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>

          {estimate?.competingSequences?.length > 0 && (
            <div className="sens-banner">
              Another active {domainLabel} campaign may overlap this audience. The contact policy will apply
              spacing, frequency, and priority rules during governance review.
            </div>
          )}
        </Panel>
      );

    if (step === 2)
      return (
        <Panel
          headingLevel={3}
          title="Workday app messages and templates"
          desc="Choose a compact Workday app outreach plan, then review timing and campaign-specific copy in each touch."
          headerRight={
            <div className="touch-preset touch-preset--header">
              <div className="field-label">Outreach plan</div>
              <div className="touch-preset__buttons" role="group" aria-label="Create predefined campaign">
                {[1, 2, 3].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={
                      "message-type-button" +
                      (campaignMode === "predefined" && plan.length === count ? " active" : "")
                    }
                    onClick={() => createPredefinedCampaign(count)}
                  >
                    {count} touch{count === 1 ? "" : "es"}
                  </button>
                ))}
                <button
                  type="button"
                  className={"message-type-button" + (campaignMode === "user-defined" ? " active" : "")}
                  onClick={startUserDefinedCampaign}
                >
                  User-defined
                </button>
                <button
                  type="button"
                  className="bi-btn-ghost"
                  disabled={plan.length === 0}
                  onClick={() => setPlan([])}
                >
                  Remove
                </button>
              </div>
            </div>
          }
        >
          {sensitivityHigh && (
            <div className="sens-banner">
              High-sensitivity cohort — use approved, benefit-oriented Workday app language.
            </div>
          )}
          <div className="touch-list">
            {plan.map((touch, i) => {
              const fieldId = (name) => `touch-${i + 1}-${name}`;
              const selected = (templates || []).find((t) => t.id === touch.templateId);
              const meta = CHANNEL_META[WORKDAY_APP_CHANNEL_KEY];
              const channelLabel = WORKDAY_APP_CHANNEL_LABEL;
              return (
                <div className="touch-card" key={i}>
                  <aside className="touch-card__aside">
                    <div className="touch-card__index">Touch {i + 1}</div>
                    <div className="touch-card__channel">
                      <span className="channel-badge" aria-hidden="true">
                        {meta.glyph}
                      </span>
                      <div className="touch-card__id">
                        <div className="channel-name">{channelLabel}</div>
                        <div className="channel-sub">{selected?.name || ""}</div>
                      </div>
                    </div>

                    <label className="field-label" htmlFor={fieldId("channel")}>
                      Communication type
                    </label>
                    <select
                      id={fieldId("channel")}
                      className="select"
                      style={{ width: "100%" }}
                      value={WORKDAY_APP_CHANNEL_KEY}
                      disabled
                    >
                      <option value={WORKDAY_APP_CHANNEL.key}>{WORKDAY_APP_CHANNEL.label}</option>
                    </select>

                    <label className="field-label" htmlFor={fieldId("timing")}>
                      Send timing
                    </label>
                    <div className="day-stepper">
                      <input
                        id={fieldId("timing")}
                        type="number"
                        min={0}
                        max={60}
                        className="input"
                        value={touch.sequenceDay}
                        aria-describedby={fieldId("timing-help")}
                        onChange={(e) => setTouch(i, { sequenceDay: Number(e.target.value) })}
                      />
                      <span className="day-suffix" id={fieldId("timing-help")}>
                        {sendTimingLabel(touch.sequenceDay)}
                      </span>
                    </div>
                  </aside>

                  <div className="touch-card__main">
                    <div className="touch-card__main-head">
                      <label className="field-label" style={{ margin: 0 }}>
                        Message sequence {selected?.sourceSequence || i + 1}
                      </label>
                      <button
                        type="button"
                        className="touch-remove"
                        title={`Remove touch ${i + 1}`}
                        aria-label={`Remove touch ${i + 1}`}
                        onClick={() => removeUserDefinedTouch(i)}
                      >
                        ✕
                      </button>
                    </div>
                    {selected && <div className="template-sequence-name">{selected.name}</div>}

                    {selected?.gaps?.length > 0 && <div className="touch-note muted">{selected.gaps[0]}</div>}

                    {selected ? (
                      <div className="template-editor">
                        <div className="template-source-row">
                          <span className="template-source-badge">Approved template</span>
                          <span className="template-source-detail">
                            {channelLabel}
                            {selected.senderName ? ` · ${selected.senderName}` : ""}
                            {selected.replyTo ? ` · reply to ${selected.replyTo}` : ""}
                          </span>
                        </div>

                        <div className="campaign-copy-heading">
                          <strong>Campaign-specific copy</strong>
                          <span>Editable</span>
                        </div>

                        <label className="field-label" htmlFor={fieldId("workday-message")}>
                          Workday app message
                          {selected.recommendedPushMaxChars
                            ? ` · ${(touch.pushMessage || "").length}/${selected.recommendedPushMaxChars}`
                            : ""}
                        </label>
                        <textarea
                          id={fieldId("workday-message")}
                          className="input"
                          rows={3}
                          value={touch.pushMessage || ""}
                          onChange={(event) => setTouch(i, { pushMessage: event.target.value })}
                        />
                        <label className="field-label" htmlFor={fieldId("safety-disclaimer")}>
                          Safety disclaimer
                        </label>
                        <textarea
                          id={fieldId("safety-disclaimer")}
                          className="input"
                          rows={2}
                          value={touch.safetyDisclaimer || ""}
                          onChange={(event) => setTouch(i, { safetyDisclaimer: event.target.value })}
                        />
                        <div style={{ fontSize: 11, color: "var(--bi-muted)", marginTop: 4 }}>
                          Changes are saved with this campaign only. The approved template remains unchanged.
                        </div>
                      </div>
                    ) : (
                      <div className="template-empty">
                        No sequence {i + 1} message is available for this goal and communication type.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {plan.length < DEFAULT_PLAN.length && (
              <button type="button" className="touch-add" onClick={addUserDefinedTouch}>
                <span className="touch-add__plus">+</span> Add touch
              </button>
            )}
          </div>
        </Panel>
      );

    return (
      <Panel
        headingLevel={3}
        title="Review and governance"
        desc="Confirm the campaign definition, then run automated checks before submitting for independent review."
        headerRight={governance && <StatusPill status={governance.overall} />}
      >
        <section className="maker-checker-notice">
          <strong>Independent approval is required</strong>
          <span>
            Automated checks do not approve, publish, or deliver this campaign. A different authorized
            reviewer must make the governance decision after submission.
          </span>
        </section>

        <section className="builder-review-settings" aria-labelledby="launch-settings-heading">
          <div className="builder-field-heading">
            <h3 id="launch-settings-heading">Launch settings</h3>
            <span className="builder-field-status editable">Editable</span>
          </div>
          <div className="restricted-search-grid">
            <p className="field-help">
              Touch 1 sends when the campaign is published; each follow-up sends its set number of days after
              publishing.
            </p>
            <label>
              Campaign priority
              <select value={priority} onChange={(event) => setPriority(Number(event.target.value))}>
                <option value={40}>General education</option>
                <option value={50}>Program outreach</option>
                <option value={60}>Enrollment</option>
                <option value={80}>Time-sensitive deadline</option>
              </select>
            </label>
          </div>
        </section>
        <div className="builder-review-grid">
          <section>
            <h3>Campaign definition</h3>
            <dl className="builder-review-list">
              <div>
                <dt>Campaign</dt>
                <dd>{name || `${programName} campaign`}</dd>
              </div>
              <div>
                <dt>Program</dt>
                <dd>{programName}</dd>
              </div>
              <div>
                <dt>Evidence period</dt>
                <dd>{selectedRun?.runMonth || "Unavailable"}</dd>
              </div>
              <div>
                <dt>Campaign goal</dt>
                <dd>{campaignGoalLabel}</dd>
              </div>
              <div>
                <dt>Aggregate audience</dt>
                <dd>
                  <MetricValue
                    value={estimate?.memberCount}
                    grain="covered people"
                    suppressed={estimate?.suppressed}
                    minimum={estimate?.minCohortSize}
                  />
                </dd>
              </div>
              <div>
                <dt>Audience qualifiers</dt>
                <dd>
                  {filters.bands.length || filters.subdomains.length
                    ? [
                        filters.bands.length ? `Priority: ${filters.bands.join(", ")}` : null,
                        filters.subdomains.length
                          ? `Condition groups: ${filters.subdomains.join(", ")}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "No optional filters added"}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>Channels and timing</h3>
            <ol className="review-touch-list">
              {plan.map((touch, index) => {
                const channelLabel =
                  enums.channels.find((channel) => channel.key === touch.channel)?.label || touch.channel;
                const templateName =
                  (templates || []).find((template) => template.id === touch.templateId)?.name ||
                  "Template unavailable";
                return (
                  <li key={`${touch.channel}-${index}`}>
                    <span>Touch {index + 1}</span>
                    <strong>{channelLabel}</strong>
                    <small>
                      {sendTimingLabel(touch.sequenceDay)} · {templateName}
                    </small>
                  </li>
                );
              })}
            </ol>
            <div className="review-launch-summary">
              <span>Start</span>
              <strong>When the campaign is published</strong>
              <small>
                priority {priority} ·{" "}
                {sensitivityHigh ? "private channels only" : "standard sensitivity"}
              </small>
            </div>
          </section>
        </div>

        <section className="automated-checks" aria-labelledby="automated-checks-heading">
          <div className="automated-checks-head">
            <div>
              <h3 id="automated-checks-heading">Automated governance checks</h3>
              <p>Checks evidence, privacy, content safety, and campaign readiness before submission.</p>
            </div>
            <button
              className="bi-btn-ghost"
              disabled={busy || !activationGoal}
              title={!activationGoal ? "Select a campaign goal before running governance" : ""}
              onClick={buildDraftAndCheck}
            >
              {busy ? "Running checks…" : governance ? "Re-run checks" : "Run automated checks"}
            </button>
          </div>

          {governance ? (
            <div className="governance-results">
              <div className="governance-results-heading">
                <StatusPill status={governance.overall} />
                <span>Draft saved automatically</span>
              </div>
              <GovernanceChecklist result={governance} />
            </div>
          ) : (
            <p className="checks-awaiting" role="status">
              Checks have not been run for the current campaign definition.
            </p>
          )}

          {governance?.overall === "fail" && (
            <p className="governance-blocking-note" role="alert">
              Resolve the failed checks before submitting. Use Back to edit the affected campaign step, then
              run the checks again.
            </p>
          )}
        </section>

        <section className="submit-review-section">
          <div>
            <h3>Submit for maker-checker review</h3>
            <p>Submission requests approval only. It does not publish the campaign or send messages.</p>
          </div>
          <button
            className="bi-btn-primary"
            disabled={busy || !draft || !governance || governance.overall === "fail"}
            onClick={submitForReview}
          >
            Submit for review
          </button>
        </section>
      </Panel>
    );
  };

  const canNext =
    (step === 0 && programId && selectedRun && !gateFailed && !campaignNameError) ||
    (step === 1 && activationGoal && estimate?.meetsMinimum) ||
    (step === 2 && planReady);

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="campaigns"
      title={
        editCampaignId
          ? `Continue campaign setup${domainLabel ? ` — ${domainLabel}` : ""}`
          : domainLabel
            ? `Create campaign — ${domainLabel}`
            : "Create campaign"
      }
      subtitle={
        headerContextParts.length
          ? headerContextParts.join(" · ")
          : "Four steps: program → cohort → channels → review and governance"
      }
    >
      <WizardSteps steps={STEPS} current={step} />
      <section className="campaign-source-panel" aria-labelledby="campaign-source-title">
        <div className="campaign-source-heading">
          <div>
            <span className="campaign-source-eyebrow">Campaign source</span>
            <h2 id="campaign-source-title">{sourceLabel}</h2>
          </div>
          <span className={`builder-field-status ${startedFromRecommendation ? "inherited" : "editable"}`}>
            {editCampaignId
              ? "Saved draft"
              : startedFromRecommendation
                ? "Inherited context"
                : "Builder context"}
          </span>
        </div>
        <dl>
          <div>
            <dt>Program</dt>
            <dd>{programName || "Not selected"}</dd>
          </div>
          <div>
            <dt>Evidence period</dt>
            <dd>{selectedRun?.runMonth || initialRunMonth || "Not selected"}</dd>
          </div>
          <div>
            <dt>Campaign goal</dt>
            <dd>{campaignGoalLabel}</dd>
          </div>
          <div>
            <dt>Aggregate audience</dt>
            <dd>
              <MetricValue
                value={activationGoal ? estimate?.memberCount : null}
                grain="covered people"
                suppressed={estimate?.suppressed}
                minimum={estimate?.minCohortSize}
                unavailableReason="Complete the cohort step"
              />
            </dd>
          </div>
        </dl>
        {editCampaignId ? (
          <p>Changes update this saved draft. Governance checks must be run again before review.</p>
        ) : (
          startedFromRecommendation && (
            <p>Program, evidence period, and recommendation remain inherited throughout this builder.</p>
          )
        )}
      </section>
      {error && <ErrorCard error={error} />}
      {stepBody()}
      <div className="builder-navigation">
        <Link className="builder-cancel-link" to={cancelRoute}>
          {draft ? "Exit builder" : "Cancel"} and return to{" "}
          {editCampaignId
            ? "campaign detail"
            : startedFromRecommendation
              ? "Opportunity Explorer"
              : "Campaigns"}
        </Link>
        <div>
          {step > 0 && (
            <button className="bi-btn-ghost" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          {step < 3 && (
            <button className="bi-btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
              {step === 0 && gateFailed ? "Evidence period blocked" : "Next"}
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}
